/**
 * F009: File Compliance Panel — Sprint 21
 *
 * Displays deal-level file compliance checklist (Module 2).
 * Features:
 * - Deal classification selector (Consumer / Commercial)
 * - BID badge (Applicable / N/A)
 * - Checklist table with checkboxes, skip-with-reason, progress
 * - Soft yellow warning banner for incomplete items (non-blocking)
 * - "Run Check" button to refresh compliance status
 * - "Generate Checklist" when no checklist items exist
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertTriangle, CheckCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// ============================================================================
// TYPES
// ============================================================================

interface ChecklistItem {
  id: string;
  itemCode: string;
  itemDescription: string;
  isCompleted: boolean;
  completedDate: string | null;
  evidenceReference: string | null;
  acknowledgementReason: string | null;
}

interface ComplianceRecord {
  id: string;
  dealId: string;
  checklistType: string;
  complianceStatus: string;
  bidApplicable: boolean;
  bidStatus: string | null;
  bidSignoffName: string | null;
  bidSignoffLicenceNumber: string | null;
  bidSignoffUserId: string | null;
  bidSignedOffAt: string | null;
  checklistItems: ChecklistItem[];
  lastCheckDate: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CheckResult {
  complianceStatus: string;
  completedItems: number;
  totalItems: number;
  warnings: Array<{ itemCode: string; message: string }>;
}

interface FileCompliancePanelProps {
  dealId: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ============================================================================
// CLASSIFICATION SELECTOR (shown when no compliance record exists)
// ============================================================================

interface ClassificationSelectorProps {
  onSelect: (classification: "consumer" | "commercial") => void;
  isPending: boolean;
}

function ClassificationSelector({
  onSelect,
  isPending,
}: ClassificationSelectorProps) {
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div className="py-8 text-center space-y-4">
      <ShieldCheck className="h-10 w-10 text-muted-foreground/50 mx-auto" />
      <div>
        <p className="text-sm font-medium text-muted-foreground">
          Deal compliance classification not set
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Select a classification to determine BID applicability and generate a
          compliance checklist.
        </p>
      </div>

      {!showOptions ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowOptions(true)}
          disabled={isPending}
          data-testid="button-classify-deal"
        >
          Classify Deal
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSelect("consumer")}
              disabled={isPending}
              data-testid="option-classification-consumer"
            >
              {isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : null}
              Consumer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSelect("commercial")}
              disabled={isPending}
              data-testid="option-classification-commercial"
            >
              Commercial
            </Button>
          </div>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            <strong>Consumer:</strong> Best Interest Duty (BID) applies. &nbsp;
            <strong>Commercial:</strong> BID not applicable.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SKIP REASON DIALOG
// ============================================================================

interface SkipReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}

function SkipReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: SkipReasonDialogProps) {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setReason("");
    }
    onOpenChange(isOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent data-testid="skip-reason-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Skip Checklist Item</AlertDialogTitle>
          <AlertDialogDescription>
            Provide a reason for skipping this compliance item. This will be
            recorded as an acknowledgement.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-2">
          <Label htmlFor="skip-reason" className="text-sm font-medium">
            Reason
          </Label>
          <Textarea
            id="skip-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter acknowledgement reason..."
            className="mt-1"
            rows={3}
            data-testid="skip-reason-input"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              handleConfirm();
            }}
            disabled={!reason.trim() || isPending}
            data-testid="button-confirm-skip"
          >
            {isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : null}
            Skip Item
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// CHECKLIST TABLE
// ============================================================================

interface ChecklistTableProps {
  items: ChecklistItem[];
  onToggleComplete: (itemId: string, isCompleted: boolean) => void;
  onSkip: (itemId: string) => void;
  isTogglingId: string | null;
}

function ChecklistTable({
  items,
  onToggleComplete,
  onSkip,
  isTogglingId,
}: ChecklistTableProps) {
  return (
    <Table data-testid="compliance-checklist-table">
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">Done</TableHead>
          <TableHead className="w-24">Code</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="w-28">Completed</TableHead>
          <TableHead className="text-right w-20">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const isSkipped = !!item.acknowledgementReason && !item.isCompleted;
          const isCompleted = item.isCompleted;
          const isToggling = isTogglingId === item.id;

          return (
            <TableRow
              key={item.id}
              data-testid={`checklist-row-${item.id}`}
              className={
                isCompleted
                  ? "bg-green-50 dark:bg-green-950/20"
                  : isSkipped
                  ? "bg-orange-50 dark:bg-orange-950/20"
                  : undefined
              }
            >
              {/* Checkbox */}
              <TableCell className="py-2 px-3">
                <Checkbox
                  checked={isCompleted}
                  onCheckedChange={(checked) =>
                    onToggleComplete(item.id, !!checked)
                  }
                  disabled={isToggling || isSkipped}
                  data-testid={`checkbox-checklist-${item.id}`}
                />
              </TableCell>

              {/* Item code */}
              <TableCell className="py-2 px-3">
                <Badge variant="outline" className="text-xs font-mono">
                  {item.itemCode}
                </Badge>
              </TableCell>

              {/* Description */}
              <TableCell className="py-2 px-3">
                <p className="text-sm">{item.itemDescription}</p>
                {isSkipped && (
                  <p className="text-xs text-orange-700 dark:text-orange-400 mt-0.5">
                    Was skipped — reason: {item.acknowledgementReason}
                  </p>
                )}
                {item.evidenceReference && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Evidence: {item.evidenceReference}
                  </p>
                )}
              </TableCell>

              {/* Completed date */}
              <TableCell className="py-2 px-3 text-xs">
                {isCompleted ? (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    {formatDate(item.completedDate)}
                  </div>
                ) : isSkipped ? (
                  <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                    Skipped
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">Pending</span>
                )}
              </TableCell>

              {/* Actions */}
              <TableCell className="py-2 px-3 text-right">
                {!isCompleted && !isSkipped && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => onSkip(item.id)}
                    disabled={isToggling}
                    data-testid={`button-skip-${item.id}`}
                  >
                    Skip
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ============================================================================
// MAIN COMPONENT: FileCompliancePanel
// ============================================================================

export function FileCompliancePanel({ dealId }: FileCompliancePanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipItemId, setSkipItemId] = useState<string | null>(null);
  const [isTogglingId, setIsTogglingId] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);

  // ----------------------------------------------------------------
  // FETCH compliance record
  // ----------------------------------------------------------------
  const { data: compliance, isLoading } = useQuery<ComplianceRecord>({
    queryKey: [`/api/compliance/deals/${dealId}/compliance`],
    enabled: !!dealId,
    retry: false,
  });

  // ----------------------------------------------------------------
  // CREATE compliance record (classify deal)
  // ----------------------------------------------------------------
  const classifyMutation = useMutation({
    mutationFn: async (classification: "consumer" | "commercial") => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/deals/${dealId}/compliance`,
        {
          dealClassification: classification,
          checklistType: "pre_submission",
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/deals/${dealId}/compliance`],
      });
      toast({ title: "Deal classified", description: "Compliance checklist generated." });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to classify deal",
        variant: "destructive",
      });
    },
  });

  // ----------------------------------------------------------------
  // TOGGLE checklist item complete/incomplete
  // ----------------------------------------------------------------
  const toggleItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      isCompleted,
    }: {
      itemId: string;
      isCompleted: boolean;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/checklist-items/${itemId}`,
        { isCompleted }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/deals/${dealId}/compliance`],
      });
      setIsTogglingId(null);
    },
    onError: (error: Error) => {
      setIsTogglingId(null);
      toast({
        title: "Error",
        description: error.message || "Failed to update checklist item",
        variant: "destructive",
      });
    },
  });

  // ----------------------------------------------------------------
  // SKIP checklist item with reason
  // ----------------------------------------------------------------
  const skipItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      reason,
    }: {
      itemId: string;
      reason: string;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/checklist-items/${itemId}`,
        { acknowledgementReason: reason }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/deals/${dealId}/compliance`],
      });
      setSkipDialogOpen(false);
      setSkipItemId(null);
      toast({ title: "Item skipped", description: "Acknowledgement reason recorded." });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to skip item",
        variant: "destructive",
      });
    },
  });

  // ----------------------------------------------------------------
  // RUN COMPLIANCE CHECK
  // ----------------------------------------------------------------
  const runCheckMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/deals/${dealId}/check`,
        {}
      );
      return response.json() as Promise<CheckResult>;
    },
    onSuccess: (result) => {
      setCheckResult(result);
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/deals/${dealId}/compliance`],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to run compliance check",
        variant: "destructive",
      });
    },
  });

  // ----------------------------------------------------------------
  // EVENT HANDLERS
  // ----------------------------------------------------------------

  const handleToggleComplete = (itemId: string, isCompleted: boolean) => {
    setIsTogglingId(itemId);
    toggleItemMutation.mutate({ itemId, isCompleted });
  };

  const handleSkipClick = (itemId: string) => {
    setSkipItemId(itemId);
    setSkipDialogOpen(true);
  };

  const handleSkipConfirm = (reason: string) => {
    if (skipItemId) {
      skipItemMutation.mutate({ itemId: skipItemId, reason });
    }
  };

  // ----------------------------------------------------------------
  // COMPUTED VALUES
  // ----------------------------------------------------------------

  const checklistItems: ChecklistItem[] = compliance?.checklistItems ?? [];
  const completedCount = checklistItems.filter(
    (item) => item.isCompleted || item.acknowledgementReason
  ).length;
  const totalCount = checklistItems.length;
  const incompleteCount = checklistItems.filter(
    (item) => !item.isCompleted && !item.acknowledgementReason
  ).length;

  const showWarning =
    checkResult !== null && incompleteCount > 0;
  const hasBidSignoff = Boolean(
    compliance?.bidSignoffName &&
    compliance?.bidSignoffLicenceNumber &&
    compliance?.bidSignedOffAt
  );

  // ----------------------------------------------------------------
  // RENDER: Loading
  // ----------------------------------------------------------------

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // ----------------------------------------------------------------
  // RENDER: No classification yet
  // ----------------------------------------------------------------

  if (!compliance) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">File Compliance</CardTitle>
        </CardHeader>
        <CardContent>
          <ClassificationSelector
            onSelect={(classification) => classifyMutation.mutate(classification)}
            isPending={classifyMutation.isPending}
          />
        </CardContent>
      </Card>
    );
  }

  // ----------------------------------------------------------------
  // RENDER: Compliance record exists
  // ----------------------------------------------------------------

  const bidApplicable = compliance.bidApplicable;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">File Compliance</CardTitle>
          <div className="flex items-center gap-2">
            {/* Classification badge */}
            <Badge variant="secondary" className="text-xs">
              {bidApplicable ? "Consumer" : "Commercial"}
            </Badge>

            {/* BID badge */}
            <Badge
              variant="secondary"
              className={`text-xs ${
                bidApplicable
                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
              }`}
              data-testid="bid-status-badge"
            >
              BID: {bidApplicable ? "Applicable" : "N/A"}
            </Badge>

            {/* Progress badge */}
            {totalCount > 0 && (
              <Badge
                variant="outline"
                className="text-xs"
                data-testid="compliance-progress-badge"
              >
                {completedCount}/{totalCount} Complete
              </Badge>
            )}

            {/* Run Check button */}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => runCheckMutation.mutate()}
              disabled={runCheckMutation.isPending || totalCount === 0}
              data-testid="button-run-check"
            >
              {runCheckMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Run Check
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {bidApplicable && (
            <div
              className="mx-4 mb-3 rounded-md border px-3 py-2"
              data-testid="bid-signoff-artefact"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">BID sign-off</p>
                  <p className="text-xs text-muted-foreground">
                    {hasBidSignoff
                      ? `${compliance.bidSignoffName} · ${compliance.bidSignoffLicenceNumber}`
                      : "No named licensed human sign-off recorded"}
                  </p>
                </div>
                <Badge variant={hasBidSignoff ? "secondary" : "outline"} className="text-xs">
                  {hasBidSignoff ? "Signed" : "Pending"}
                </Badge>
              </div>
              {hasBidSignoff && (
                <dl className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">Signed at</dt>
                    <dd>{formatDateTime(compliance.bidSignedOffAt)}</dd>
                  </div>
                  {compliance.bidSignoffUserId && (
                    <div>
                      <dt className="text-muted-foreground">Signer user ID</dt>
                      <dd className="font-mono">{compliance.bidSignoffUserId}</dd>
                    </div>
                  )}
                </dl>
              )}
            </div>
          )}

          {/* Soft warning banner (yellow, non-blocking) */}
          {showWarning && (
            <div
              className="mx-4 mb-3 flex items-center justify-between rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-700 px-3 py-2"
              data-testid="compliance-warning-banner"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                <span className="text-sm text-yellow-800 dark:text-yellow-300">
                  {incompleteCount} compliance item
                  {incompleteCount !== 1 ? "s" : ""} incomplete. Review before
                  submission.
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-yellow-400 text-yellow-800 hover:bg-yellow-100"
                onClick={() => setCheckResult(null)}
                data-testid="button-acknowledge-proceed"
              >
                Acknowledge &amp; Proceed
              </Button>
            </div>
          )}

          {/* Checklist table */}
          {totalCount === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No checklist items yet.</p>
              <p className="text-xs mt-1">
                Click &ldquo;Classify Deal&rdquo; or &ldquo;Generate
                Checklist&rdquo; to start.
              </p>
            </div>
          ) : (
            <ChecklistTable
              items={checklistItems}
              onToggleComplete={handleToggleComplete}
              onSkip={handleSkipClick}
              isTogglingId={isTogglingId}
            />
          )}
        </CardContent>
      </Card>

      {/* Skip Reason Dialog */}
      <SkipReasonDialog
        open={skipDialogOpen}
        onOpenChange={setSkipDialogOpen}
        onConfirm={handleSkipConfirm}
        isPending={skipItemMutation.isPending}
      />
    </>
  );
}
